<template>
  <div class="list">
    <div v-if="content.headline" class="section-label list__label">
      {{ content.headline }}
    </div>

    <div class="wrapper">
      <h2
        v-if="content.title"
        ref="titleRef"
        class="list__title h2-custom"
        v-html="useLineBreak(content.title)"
      ></h2>
    </div>

    <template v-for="(service, index) in content.items" :key="service.title">
      <div
        :class="{ 'is-reversed': index % 2 === 1 }"
        class="list__item wrapper"
      >
        <span class="list__item__count"> {{ index + 1 }}. </span>

        <div class="list__item-content">
          <span v-if="service.headline" class="info-value list__item__headline">
            {{ service.headline }}
          </span>

          <h2
            v-if="service.title"
            class="h2-custom list__item__title"
            v-html="useLineBreak(service.title)"
          ></h2>

          <div class="list__item__text">
            <div class="p-medium" v-html="service.htmltext"></div>

            <div v-if="service.services?.length" class="list__item__services">
              <GBtn
                v-for="btn in service.services"
                :key="btn.url"
                :url="btn.url"
                :icon="btn.url ? 'ui-full-arrow' : ''"
                :tag="btn.url ? 'GAction' : 'span'"
                size="small"
              >
                {{ btn.title }}
              </GBtn>
            </div>
          </div>

          <div class="list__item__images stacked">
            <DraggableImages active-after="s">
              <Picture
                v-for="picture in service.pictures"
                :key="picture.src"
                :content="{ picture }"
                :style="`transform: rotate(${deg(index % 2 !== 0)})`"
                sizes="1280px"
                data-draggable
                :is-polaroid="true"
                class="list__item__image"
              ></Picture>
            </DraggableImages>
          </div>
        </div>
      </div>

      <div class="list__item__images full">
        <!-- <div> -->
        <Picture
          v-for="picture in service.pictures"
          :key="picture.src"
          :content="{ picture }"
          :style="`transform: rotate(${deg(index % 2 !== 0)})`"
          :is-polaroid="true"
          class="list__item__image"
        ></Picture>
        <!-- </div> -->
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { PropType, ref, onMounted } from 'vue'

import { useLineBreak } from '@/utils/filters'
import { Picture as PictureType, Uri } from '@/types'

import Picture from '@/components/global/Picture.vue'
import DraggableImages from '@/components/global/DraggableImages.vue'
import { useResize } from '@/utils/aware'
import { createSplitTitle, SplitTitle } from '@/animations/splitTitle'
import isMobile from 'ismobilejs'

const titleRef = ref<HTMLElement>()

interface ServiceList {
  title: string
  headline: string
  items: {
    headline: string
    title: string
    htmltext: string
    services?: Uri[]
    pictures: PictureType[]
  }[]
}

defineProps({
  content: {
    type: Object as PropType<ServiceList>,
    required: true,
  },
})

// const isGreaterXxl = ref(useResponsive('isGreater', 'l'))

// useResize(() => {
//   isGreaterXxl.value = useResponsive('isGreater', 'l')
// })

const deg = (negative: boolean) => {
  const deg = negative
    ? gsap.utils.random(-2, -20, 1)
    : gsap.utils.random(2, 20, 1)

  return `${deg}deg`
}

let splitTitle: SplitTitle

const onWindowResize = () => {
  isMobile()
}

onMounted(() => {
  if (!titleRef.value) {
    return
  }

  useResize(onWindowResize.bind(isMobile()))
  if (!isMobile().phone) {
    splitTitle = createSplitTitle(titleRef.value)
    splitTitle.intro.play()
  }
})
</script>

<style lang="scss" scoped>
// .list {
//   color: var(--c-foreground);
// }

.list__label {
  // prettier-ignore
  @include fluid(margin-bottom,(xxs: 70px, xxl: 110px));

  text-align: center;
}

.list__title {
  :deep {
    .split-line {
      width: fit-content;
      &:last-child {
        @include mq(nav) {
          margin-left: auto;
        }
      }
    }
  }

  @include fluid(
    margin-top,
    (
      xxs: 40px,
      xxl: 80px,
    )
  );
  @include fluid(
    margin-bottom,
    (
      xxs: 40px,
      xxl: 120px,
    )
  );
}

.list__item {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 2rem;
  }

  @include mq(s) {
    flex-direction: row;
    align-items: flex-start;
  }

  @include mq(nav) {
    margin-bottom: 13rem;

    &.is-reversed {
      flex-direction: row-reverse;
    }
  }
}

.list__item__count {
  @extend %fw-air;

  // prettier-ignore
  @include fluid(font-size,(xxs: 70px, xxl: 240px));

  // position: relative;
  // z-index: 4;
  margin-bottom: 4rem;
  font-family: $ff-alt;
  line-height: 1;

  @include mq(s) {
    flex-shrink: 0;
    width: col(2);
    margin-right: col(1);
    margin-bottom: 0;
    text-align: center;
    border-right: 1px solid var(--c-foreground);
  }

  @include mq(nav) {
    margin-left: col(1);

    .is-reversed & {
      border-right: 0;
      border-left: 1px solid var(--c-foreground);
    }
  }
}

.list__item-content {
  display: flex;
  flex-direction: column;

  @include mq(s, nav) {
    width: col(9);
  }
}

.list__item__title,
.list__item__headline {
  position: relative;
  z-index: 2;
  margin-bottom: 2rem;
}

.list__item__text,
.list__item__headline {
  position: relative;
  z-index: 2;

  @include mq(nav) {
    .is-reversed & {
      margin-left: col(2, 8);
    }
  }
}

.list__item__headline {
  @include mq(nav) {
    .is-reversed & {
      text-align: center;
    }
  }
}

.list__item__title {
  @include mq(nav) {
    .is-reversed & {
      text-align: right;
    }
  }
}

.list__item__text {
  :deep(p:not(p:last-child)) {
    margin-bottom: 3rem;
  }

  @include mq(nav) {
    width: col(6, 8);
  }

  @include mq(xxl) {
    width: col(5, 8);
  }
}

.list__item__services {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2rem;

  :deep(span) {
    /* stylelint-disable-next-line declaration-no-important */
    cursor: default !important;
  }
}

.list__item__images {
  // prettier-ignore
  @include fluid(margin-bottom,(xxs: 30px, xxl: 130px));
  @include scrollbars($foreground-color: var(--c-foreground));
  &::-webkit-scrollbar-track {
    margin-left: calc(9.6153846154vw - 10.7692307692px);
    margin-right: calc(9.6153846154vw - 10.7692307692px);
  }
  display: flex;
  align-items: flex-start;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 2rem 1rem;
  @include mq($until: nav) {
    margin: 60px auto;
    align-items: center;
    padding: 0 calc(9.6153846154vw - 10.7692307692px) 34px
      calc(9.6153846154vw - 10.7692307692px);
  }
  &.stacked {
    display: none;
  }

  @include mq(nav) {
    position: absolute;
    bottom: 0;
    left: 0;
    overflow-x: visible;
    overflow-y: visible;
    width: 100%;
    height: 100%;
    margin: 0;

    &.full {
      display: none;
    }

    &.stacked {
      z-index: 10;
      pointer-events: none;
      display: flex;
    }
  }
}

.list__item__image {
  width: 100%;
  min-width: 28rem;
  max-width: 46rem;
  margin-right: 2rem;

  @include mq($until: nav) {
    pointer-events: none;
    /* stylelint-disable-next-line declaration-no-important */
    transform: none !important;
  }

  @include mq(nav) {
    position: absolute;
    z-index: 3;
    bottom: 0;
    left: -20rem;
    // height: 100%;
    // min-height: 25rem;
    // max-height: 36rem;

    .is-reversed & {
      right: -25rem;
      left: auto;
    }
  }

  @include mq(xxl) {
    left: -10rem;

    .is-reversed & {
      right: 0rem;
    }
  }
}
</style>
